/**
 * CodeMirror Spellcheck Extension
 *
 * Adds spellchecking decorations to the CodeMirror editor using nspell
 * with context menu support for spelling suggestions
 */

import { ViewPlugin, Decoration, EditorView } from '@codemirror/view';
import { RangeSetBuilder, Annotation } from '@codemirror/state';
import { spellcheckService } from '../../services/spellcheckService';

/**
 * Annotation to force spellcheck refresh
 */
const forceSpellcheckRefresh = Annotation.define();

/**
 * Extract words from text with their positions
 */
function extractWords(text, offset = 0) {
    const words = [];
    // Match words containing letters, hyphens, and apostrophes (numbers make words invalid)
    const wordRegex = /\b[a-zA-Z]+(?:['-]?[a-zA-Z0-9]+)*\b/g;
    let match;

    while ((match = wordRegex.exec(text)) !== null) {
        words.push({
            text: match[0],
            from: offset + match.index,
            to: offset + match.index + match[0].length
        });
    }

    return words;
}

/**
 * Create spellcheck decorations for the view
 */
function createSpellcheckDecorations(view) {
    if (!spellcheckService.isInitialized()) {
        return Decoration.none;
    }

    const builder = new RangeSetBuilder();
    const { state } = view;

    for (const { from, to } of view.visibleRanges) {
        const text = state.doc.sliceString(from, to);
        const words = extractWords(text, from);

        for (const word of words) {
            if (!spellcheckService.correct(word.text)) {
                builder.add(
                    word.from,
                    word.to,
                    Decoration.mark({
                        class: 'cm-spellcheck-error',
                        attributes: { 'data-word': word.text }
                    })
                );
            }
        }
    }

    return builder.finish();
}

/**
 * Spellcheck view plugin
 */
const spellcheckViewPlugin = ViewPlugin.fromClass(
    class {
        constructor(view) {
            this.decorations = createSpellcheckDecorations(view);
        }

        update(update) {
            // Recalculate decorations if document or viewport changed, or if forced
            if (update.docChanged || update.viewportChanged || update.transactions.some(tr => tr.annotation(forceSpellcheckRefresh))) {
                this.decorations = createSpellcheckDecorations(update.view);
            }
        }
    },
    {
        decorations: v => v.decorations
    }
);

/**
 * Find misspelled word at a given position
 */
function findMisspelledWordAt(view, pos) {
    const { state } = view;
    const line = state.doc.lineAt(pos);
    const lineText = line.text;
    const posInLine = pos - line.from;

    const words = extractWords(lineText, line.from);

    for (const word of words) {
        if (posInLine >= word.from - line.from && posInLine <= word.to - line.from) {
            if (!spellcheckService.correct(word.text)) {
                return word;
            }
        }
    }

    return null;
}

/**
 * Create and show context menu with spelling suggestions
 */
function showSpellingContextMenu(view, word, event) {
    event.preventDefault();

    // Remove any existing context menu
    const existingMenu = document.querySelector('.spellcheck-context-menu');
    if (existingMenu) {
        existingMenu.remove();
    }

    // Get suggestions
    const suggestions = spellcheckService.suggest(word.text);

    // Create context menu
    const menu = document.createElement('div');
    menu.className = 'spellcheck-context-menu';
    menu.style.position = 'fixed';
    menu.style.left = `${event.clientX}px`;
    menu.style.top = `${event.clientY}px`;
    menu.style.zIndex = '10000';
    menu.style.background = 'var(--color-surface-primary, #ffffff)';
    menu.style.border = '1px solid var(--color-border, #e0e0e0)';
    menu.style.borderRadius = 'var(--border-radius-md, 4px)';
    menu.style.boxShadow = 'var(--shadow-lg, 0 4px 12px rgba(0,0,0,0.15))';
    menu.style.minWidth = '150px';
    menu.style.maxWidth = '250px';
    menu.style.padding = '4px 0';

    // Add suggestions (max 5)
    const maxSuggestions = 5;
    const displaySuggestions = suggestions.slice(0, maxSuggestions);

    if (displaySuggestions.length > 0) {
        displaySuggestions.forEach(suggestion => {
            const item = document.createElement('div');
            item.className = 'spellcheck-menu-item';
            item.textContent = suggestion;
            item.style.padding = '6px 12px';
            item.style.cursor = 'pointer';
            item.style.fontSize = 'var(--font-size-sm, 14px)';
            item.style.color = 'var(--color-text-primary, #000000)';

            item.addEventListener('mouseenter', () => {
                item.style.background = 'var(--color-surface-hover, #f5f5f5)';
            });

            item.addEventListener('mouseleave', () => {
                item.style.background = 'transparent';
            });

            item.addEventListener('click', () => {
                // Replace the misspelled word with the suggestion
                view.dispatch({
                    changes: {
                        from: word.from,
                        to: word.to,
                        insert: suggestion
                    }
                });
                menu.remove();
            });

            menu.appendChild(item);
        });

        // Add separator
        const separator = document.createElement('div');
        separator.style.height = '1px';
        separator.style.background = 'var(--color-border, #e0e0e0)';
        separator.style.margin = '4px 0';
        menu.appendChild(separator);
    } else {
        // No suggestions available
        const noSuggestions = document.createElement('div');
        noSuggestions.textContent = 'No suggestions';
        noSuggestions.style.padding = '6px 12px';
        noSuggestions.style.fontSize = 'var(--font-size-sm, 14px)';
        noSuggestions.style.color = 'var(--color-text-tertiary, #999999)';
        noSuggestions.style.fontStyle = 'italic';
        menu.appendChild(noSuggestions);

        const separator = document.createElement('div');
        separator.style.height = '1px';
        separator.style.background = 'var(--color-border, #e0e0e0)';
        separator.style.margin = '4px 0';
        menu.appendChild(separator);
    }

    // Add "Add to dictionary" option
    const addToDictionary = document.createElement('div');
    addToDictionary.className = 'spellcheck-menu-item';
    addToDictionary.textContent = 'Add to dictionary';
    addToDictionary.style.padding = '6px 12px';
    addToDictionary.style.cursor = 'pointer';
    addToDictionary.style.fontSize = 'var(--font-size-sm, 14px)';
    addToDictionary.style.color = 'var(--color-text-primary, #000000)';

    addToDictionary.addEventListener('mouseenter', () => {
        addToDictionary.style.background = 'var(--color-surface-hover, #f5f5f5)';
    });

    addToDictionary.addEventListener('mouseleave', () => {
        addToDictionary.style.background = 'transparent';
    });

    addToDictionary.addEventListener('click', () => {
        spellcheckService.addWord(word.text);
        // Force view update to recalculate decorations
        view.dispatch({
            annotations: forceSpellcheckRefresh.of(true)
        });
        menu.remove();
    });

    menu.appendChild(addToDictionary);

    // Add menu to document
    document.body.appendChild(menu);

    // Close menu when clicking outside
    const closeMenu = (e) => {
        if (!menu.contains(e.target)) {
            menu.remove();
            document.removeEventListener('mousedown', closeMenu);
            document.removeEventListener('contextmenu', closeMenu);
        }
    };

    setTimeout(() => {
        document.addEventListener('mousedown', closeMenu);
        document.addEventListener('contextmenu', closeMenu);
    }, 100);
}

/**
 * Handle context menu event
 */
const contextMenuHandler = EditorView.domEventHandlers({
    contextmenu(event, view) {
        if (!spellcheckService.isInitialized()) {
            return false;
        }

        const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
        if (pos === null) {
            return false;
        }

        const word = findMisspelledWordAt(view, pos);
        if (word) {
            showSpellingContextMenu(view, word, event);
            return true;
        }

        return false;
    }
});

/**
 * Spellcheck theme
 */
const spellcheckTheme = {
    '.cm-spellcheck-error': {
        textDecoration: 'underline wavy red',
        textDecorationSkipInk: 'none',
        cursor: 'pointer'
    }
};

/**
 * Complete spellcheck extension
 */
export function createSpellcheckExtension() {
    return [spellcheckViewPlugin, contextMenuHandler];
}

export { spellcheckTheme };
